import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import PageShell from '../../Components/PageShell';
import AdminNav from './AdminNav';
import { STATUS_LABEL } from './subscriptionStatus';

/* Which orgs to show, and the sortable columns. Choices are remembered in
 * this browser. */
const FILTERS = [
    { key: 'all', label: 'All', match: () => true },
    { key: 'trial', label: 'Trial', match: (o) => o.effective_status === 'trialing' },
    { key: 'paying', label: 'Paying', match: (o) => o.effective_status === 'paid' || o.effective_status === 'comped' },
];
const SORTS = {
    last_seen: (o) => o.last_seen_at,
    searches: (o) => o.search_count || 0,
    joined: (o) => o.created_at,
    trial_ends: (o) => (o.effective_status === 'trialing' ? o.trial_ends_at : null),
};
const PREFS_KEY = 'admin_orgs_view';
const loadPrefs = () => {
    try {
        return { filter: 'all', sort: 'joined', dir: 'desc', ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
    } catch {
        return { filter: 'all', sort: 'joined', dir: 'desc' };
    }
};

const OrgsPage = () => {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [prefs, setPrefs] = useState(loadPrefs);

    useEffect(() => {
        try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
    }, [prefs]);

    // Filter, then sort. Blank dates (never seen / no trial) always go last;
    // counts are numbers already, and 0 is a real value, not a blank.
    const shown = useMemo(() => {
        const filter = FILTERS.find((f) => f.key === prefs.filter) || FILTERS[0];
        const value = SORTS[prefs.sort] || SORTS.joined;
        const sign = prefs.dir === 'asc' ? 1 : -1;
        const asNumber = (v) => (typeof v === 'number' ? v : v ? new Date(v).getTime() : null);
        return orgs.filter(filter.match).sort((a, b) => {
            const va = asNumber(value(a));
            const vb = asNumber(value(b));
            if (va === null && vb === null) return 0;
            if (va === null) return 1;
            if (vb === null) return -1;
            return (va - vb) * sign;
        });
    }, [orgs, prefs]);

    // Click a heading to sort by it; click again to reverse. Dates start
    // newest-first, except trial ends, which starts soonest-first.
    const sortBy = (key) => setPrefs((p) => ({
        ...p,
        sort: key,
        dir: p.sort === key ? (p.dir === 'asc' ? 'desc' : 'asc') : (key === 'trial_ends' ? 'asc' : 'desc'),
    }));
    const SortTh = ({ col, children }) => (
        <th className="admin-orgs-sortable" aria-sort={prefs.sort === col ? (prefs.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" onClick={() => sortBy(col)}>
                {children}
                <span className="admin-orgs-sort-arrow">
                    {prefs.sort === col ? (prefs.dir === 'asc' ? '↑' : '↓') : '↕'}
                </span>
            </button>
        </th>
    );

    useEffect(() => {
        api.get('admin/organisations')
            .then(setOrgs)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const fmt = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    // "Today", "Yesterday", "5 days ago", then the date — full date on hover.
    const ago = (dateStr) => {
        if (!dateStr) return null;
        const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
        if (days <= 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 14) return `${days} days ago`;
        return fmt(dateStr);
    };

    return (
        <PageShell wrap={false}>
            <AdminNav />
            <div className="fs admin-orgs-page admin-orgs-page--wide">
                <div className="admin-orgs-header">
                    <h1 className="admin-orgs-title">Organisations</h1>
                    <span className="admin-orgs-count">
                        {loading ? '' : `${shown.length} of ${orgs.length}`}
                    </span>
                    <div className="admin-orgs-filter" role="group" aria-label="Show">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                className={prefs.filter === f.key ? 'on' : ''}
                                onClick={() => setPrefs((p) => ({ ...p, filter: f.key }))}
                            >
                                {f.label} <span>{orgs.filter(f.match).length}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {error && (
                    <div className="admin-orgs-error">Error loading organisations: {error}</div>
                )}

                {loading ? (
                    <div className="admin-orgs-loading">Loading…</div>
                ) : (
                    <div className="admin-orgs-table-wrap">
                        <table className="admin-orgs-table">
                            <thead>
                                <tr>
                                    <th>Organisation</th>
                                    <th>Owner email</th>
                                    <th>Phone</th>
                                    <th>Subscription</th>
                                    <th>Stripe</th>
                                    <SortTh col="trial_ends">Trial ends</SortTh>
                                    <th>Members</th>
                                    <SortTh col="last_seen">Last seen</SortTh>
                                    <SortTh col="searches">Searches</SortTh>
                                    <SortTh col="joined">Joined</SortTh>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="admin-orgs-empty">No organisations found</td>
                                    </tr>
                                ) : (
                                    shown.map((org) => (
                                        <tr key={org.id}>
                                            <td className="admin-orgs-name">{org.name}</td>
                                            <td className="admin-orgs-email">
                                                {org.owner_email
                                                    ? <a href={`mailto:${org.owner_email}`} title={org.owner_name || ''}>{org.owner_email}</a>
                                                    : '—'}
                                            </td>
                                            <td className="admin-orgs-email">
                                                {org.owner_phone
                                                    ? <a href={`tel:${org.owner_phone.replace(/[^\d+]/g, '')}`}>{org.owner_phone}</a>
                                                    : '—'}
                                            </td>
                                            <td className="admin-orgs-sub">{STATUS_LABEL[org.effective_status] || org.effective_status}</td>
                                            <td>
                                                {org.stripe_status
                                                    ? <span className={`badge ${org.stripe_status === 'active' ? 'success' : 'warm'}`}>{org.stripe_status}</span>
                                                    : org.stripe_customer_id
                                                        ? <span className="badge cold">inactive</span>
                                                        : '—'}
                                            </td>
                                            <td className="admin-orgs-date">
                                                {org.effective_status === 'paid' || org.effective_status === 'comped'
                                                    ? '—'
                                                    : org.effective_status === 'free' && org.search_credits !== null && org.search_credits !== undefined
                                                        // Signed up on free searches: no trial to show.
                                                        ? `No trial · ${org.search_credits} free search${org.search_credits === 1 ? '' : 'es'} left`
                                                        : org.trial_ends_at
                                                        ? fmt(org.trial_ends_at)
                                                        : 'Never expires'}
                                            </td>
                                            <td className="admin-orgs-members">{org.member_count}</td>
                                            <td
                                                className="admin-orgs-date"
                                                title={org.last_seen_at ? `${new Date(org.last_seen_at).toLocaleString('en-GB')} · ${org.last_seen_by}` : 'Not seen since tracking began (28 Aug 2026)'}
                                            >
                                                {ago(org.last_seen_at) || '—'}
                                                {org.last_seen_by && org.member_count > 1 && (
                                                    <span className="admin-orgs-who">{org.last_seen_by}</span>
                                                )}
                                            </td>
                                            <td
                                                className="admin-orgs-date"
                                                title={`Trader searches since tracking began (9 Sep 2026); map moves and New to Trade not counted${org.last_search_at ? ` · last ${new Date(org.last_search_at).toLocaleString('en-GB')}` : ''}`}
                                            >
                                                {org.search_count || 0}
                                                {org.last_search_at && (
                                                    <span className="admin-orgs-who">Last: {ago(org.last_search_at)}</span>
                                                )}
                                            </td>
                                            <td className="admin-orgs-date">{fmt(org.created_at)}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary admin-orgs-view-btn"
                                                    onClick={() => navigate(`/admin/orgs/${org.id}`)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </PageShell>
    );
};

export default OrgsPage;
